/**
 * Pairing-code normalization, shared by the code-login screen and its
 * tests. Mirrors memoneo's `backend/app/routers/pairing.py`
 * `_normalize_code` (strip to the base32-ish alphabet, uppercase) — the
 * backend accepts "ABCD-EFGH", "abcdefgh", or with stray spaces, so the
 * client does the same before ever hitting the network. The alphabet check
 * isn't enforced client-side (unlike the backend's `_CODE_ALPHABET`,
 * excluding 0/O/1/I/L): letting a mistyped ambiguous character through just
 * means a 404 from `pairing/consume` instead of a client-side rejection,
 * which is friendlier than guessing at intent.
 */

/** Real codes are 8 characters (memoneo); the mock's fake code is 6 — accept
 *  both so the same field works against either backend. */
export const PAIRING_CODE_MIN_LEN = 6;
export const PAIRING_CODE_MAX_LEN = 8;

export function normalizePairingCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function isPairingCodeComplete(raw: string): boolean {
  const normalized = normalizePairingCode(raw);
  return normalized.length >= PAIRING_CODE_MIN_LEN && normalized.length <= PAIRING_CODE_MAX_LEN;
}

/** Cosmetic grouping for display only ("ABCDEFGH" -> "ABCD-EFGH"); codes
 *  shorter than 5 characters (the mock's) are shown as-is. */
export function formatPairingCode(code: string): string {
  const normalized = normalizePairingCode(code);
  if (normalized.length <= 4) return normalized;
  return `${normalized.slice(0, 4)}-${normalized.slice(4)}`;
}
